import React, {useState, useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import './chatbox.css';
import TextField from '@mui/material/TextField';
import {sendChatMessage} from "../../api";
import {containsProfanity} from '../../utils/profanity.js';

// Map of chat message `type` field to a CSS class. Anything not listed renders
// as a normal user message. See chatbox.css for the colors.
const MESSAGE_TYPE_CLASS = {
    'system':         'ChatMessage--system',
    'system-kick':    'ChatMessage--system-kick',
    'round-info':     'ChatMessage--round-info',
    'round-end':      'ChatMessage--round-end',
    'period-end':     'ChatMessage--period-end',
    'guess-correct':  'ChatMessage--guess-correct',
    'close-guess':    'ChatMessage--close-guess',
};

const textFieldSx = {
    width: "100%",
    m: 0,
    p: 0,
    borderRadius: 0,
    border: "2px solid #eee",
    boxSizing: "border-box",
    background: "#484c64",
    '& fieldset': { border: 'none' },
    '& .MuiInputBase-input': { padding: '3px 6px', color: '#ccc', background: '#484c64' },
    '& .MuiInputBase-input::placeholder': { userSelect: 'none', pointerEvents: 'none' },
};

const inputSx = {
    m: 0,
    p: 0,
    borderRadius: 0,
};

function useChatInput({isGuessMode, isDrawer, roundActive, rateLimited, setRateLimited, user, isSpectator, correctGuessers}) {
    const {t} = useTranslation();
    const [fieldValue, setFieldValue] = useState("");
    const lastSendRef = useRef(0);
    const [localRateLimited, setLocalRateLimited] = useState(false);
    const hasGuessedCorrectly = isGuessMode && roundActive && correctGuessers && correctGuessers.has(user);
    // chatBlocked: blocks sending. inputDisabled: also greys out / unmounts the field.
    // We deliberately keep the input mounted+enabled when the only reason is
    // hasGuessedCorrectly so the user does not lose focus right after pressing
    // Enter on a correct guess.
    const chatBlocked = isSpectator || (isGuessMode && isDrawer && roundActive) || hasGuessedCorrectly;
    const inputDisabled = isSpectator || (isGuessMode && isDrawer && roundActive);
    const showRateLimited = localRateLimited || rateLimited;

    const handleChange = e => setFieldValue(e.target.value);

    const handleKey = event => {
        if (event.key === "Enter" && fieldValue.length !== 0) {
            if (chatBlocked) return;
            const now = Date.now();
            if (now - lastSendRef.current < 1000) {
                setLocalRateLimited(true);
                setTimeout(() => setLocalRateLimited(false), 1000);
                return;
            }
            lastSendRef.current = now;
            if (!containsProfanity(fieldValue, {mode: 'normal'})) {
                sendChatMessage({message: fieldValue, user});
            }
            setFieldValue("");
        }
    };

    const placeholder = isSpectator
        ? t('chatBox.spectatorMode', 'Spectating')
        : hasGuessedCorrectly
            ? t('chatBox.alreadyGuessed', 'Already guessed!')
            : chatBlocked
                ? t('chatBox.drawerCannotChat')
                : (isGuessMode && roundActive && !isDrawer)
                    ? t('chatBox.chatOrGuess')
                    : t('chatBox.typeMessage');

    return {fieldValue, handleChange, handleKey, chatBlocked, inputDisabled, showRateLimited, placeholder};
}

// Mobile browsers auto-scroll the focused input into view when the soft
// keyboard slides in. The viewport meta uses `interactive-widget=overlays-content`
// so the keyboard overlays the page without resizing or shifting the viewport,
// but we still snap to top defensively for browsers that ignore the hint.
function handleChatInputFocus() {
    if (typeof window === 'undefined') return;
    const snap = () => window.scrollTo(0, 0);
    snap();
    requestAnimationFrame(snap);
    setTimeout(snap, 100);
    setTimeout(snap, 300);
}

export function ChatInput(props) {
    const {t} = useTranslation();
    const {fieldValue, handleChange, handleKey, inputDisabled, showRateLimited, placeholder} = useChatInput(props);
    return (
        <div style={{position: 'relative', width: '100%'}}>
            <TextField
                placeholder={placeholder}
                id="outlined-margin-dense-mobile"
                sx={textFieldSx}
                helperText=""
                size="small"
                value={fieldValue}
                onChange={handleChange}
                onKeyPress={handleKey}
                onFocus={handleChatInputFocus}
                disabled={inputDisabled}
                variant="standard"
                InputProps={{ disableUnderline: true, sx: inputSx }}
                fullWidth
            />
            {showRateLimited && (
                <span style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    fontSize: 11, color: '#ff6b6b', pointerEvents: 'none',
                }}>{t('chatBox.slowDown')}</span>
            )}
        </div>
    );
}

export default function ChatBox(props) {
    const {t} = useTranslation();
    const {fieldValue, handleChange, handleKey, inputDisabled, showRateLimited, placeholder} = useChatInput(props);

    useEffect(()=>{
        // Scroll to bottom when new messages arrive
        var elem = document.getElementById('messages');
        if (elem)
            elem.scrollTop = elem.scrollHeight;
    }, [props.chatMessages]);

    return(
        <div className={`ChatBox Col${props.compact ? ' ChatBox--compact' : ''}`}>
            <div className="MsgContainer Col" id="messages">
                {props.chatMessages.map((m, i) => {
                const isBot = !!(m.user && m.isBot);
                const typeClass = MESSAGE_TYPE_CLASS[m.type] || '';
                const isSystemLike = typeClass && !m.user;
                const message = m.i18nKey
                    ? t(m.i18nKey, {...(m.i18nParams || {}), defaultValue: m.message})
                    : m.message;
                return <p className={`ChatMessage${typeClass ? ' ' + typeClass : ''}`} key={i} >
                    {!isSystemLike && (
                        <span style={{color: isBot ? "#a8c8ff" : "#ccf", paddingRight: props.compact ? "0.3em" : "0.7em", fontSize: props.compact ? "11px" : "16px"}} className="ChatMessageUser">
                            {(m.user ? (m.user+""):"").replace(" ", '\xa0')}
                            {isBot && <span className="ChatAiBadge">AI</span>}
                        </span>
                    )}
                    {message}
                </p>
            })}
            </div>

            {!props.compact && !inputDisabled && (
            <div style={{position: 'relative'}}>
                <TextField
                    placeholder={placeholder}
                    id="outlined-margin-dense"
                    sx={textFieldSx}
                    helperText=""
                    size="small"
                    value={fieldValue}
                    onChange={handleChange}
                    onKeyPress={handleKey}
                    onFocus={handleChatInputFocus}
                    disabled={inputDisabled}
                    variant="standard"
                    InputProps={{
                        disableUnderline: true,
                        sx: inputSx
                    }}
                    fullWidth
                />
                {showRateLimited && (
                    <span style={{
                        position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                        fontSize: 11, color: '#ff6b6b', pointerEvents: 'none',
                    }}>
                        {t('chatBox.slowDown')}
                    </span>
                )}
            </div>
            )}
        </div>
    )
}